import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Space } from 'antd';
import {
  faArrowRightArrowLeft,
  faArrowRight,
  faArrowLeft,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { useDataFilter } from '../../../hooks/SimViewer/useDataFilter';
import { useAggregationConfig } from '../../../hooks/SimViewer/useAggregationConfig';
import { includes } from '../../../utils/array';
import { FeatureIcon } from './FeatureIcon';

type Props = {
  icon: ReactNode;
  label: string;
  extra?: ReactNode;
};

const StopByPanel = ({ icon, label, extra, ...props }: Props): ReactNode => {
  const { t } = useTranslation();
  const { aggregationConfig, getAvailableAggregationTypes, startAggregation } =
    useAggregationConfig();
  const { dataFilter, setStopBy } = useDataFilter();

  return (
    <Collapse.Panel
      key="stop_by"
      header={
        <Space>
          {icon}
          {label}
          {extra}
        </Space>
      }
      {...props}
    >
      <Content
        role="button"
        style={{ marginLeft: '16px', marginTop: '-4px' }}
        onClick={() => {
          if (dataFilter.stopBy === 'before') {
            setStopBy(null);
          } else {
            setStopBy('before');

            // 前立ち寄りの場合は出発地点に変える
            if (
              aggregationConfig?.dimension !== 'start' ||
              !includes(['count_per_area', 'count'], aggregationConfig.metric)
            ) {
              const metric = includes(
                ['count_per_area', 'count'],
                aggregationConfig?.metric
              )
                ? aggregationConfig.metric
                : 'count_per_area';
              const availableTypes = getAvailableAggregationTypes(
                'start',
                metric
              );
              startAggregation({
                type: includes(availableTypes, aggregationConfig?.type)
                  ? aggregationConfig.type
                  : availableTypes[0],
                dimension: 'start',
                metric: metric,
              });
            }
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ marginRight: '8px' }}>
            <FeatureIcon icon={faArrowLeft} />
          </div>
          {t('stopByBefore')}
        </div>
        {dataFilter.stopBy === 'before' && (
          <FeatureIcon icon={faCheck} size="14px" />
        )}
      </Content>
      <Content
        role="button"
        style={{ marginLeft: '16px' }}
        onClick={() => {
          if (dataFilter.stopBy === 'after') {
            setStopBy(null);
          } else {
            setStopBy('after');

            // 後立ち寄りの場合は到着地点に変える
            if (
              aggregationConfig?.dimension !== 'end' ||
              !includes(['count_per_area', 'count'], aggregationConfig.metric)
            ) {
              const metric = includes(
                ['count_per_area', 'count'],
                aggregationConfig?.metric
              )
                ? aggregationConfig.metric
                : 'count_per_area';
              const availableTypes = getAvailableAggregationTypes(
                'end',
                metric
              );
              startAggregation({
                type: includes(availableTypes, aggregationConfig?.type)
                  ? aggregationConfig.type
                  : availableTypes[0],
                dimension: 'end',
                metric: metric,
              });
            }
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ marginRight: '8px' }}>
            <FeatureIcon icon={faArrowRight} />
          </div>
          {t('stopByAfter')}
        </div>
        {dataFilter.stopBy === 'after' && (
          <FeatureIcon icon={faCheck} size="14px" />
        )}
      </Content>
      <Content
        role="button"
        style={{ marginLeft: '16px' }}
        onClick={() => {
          if (dataFilter.stopBy === 'all') {
            setStopBy(null);
          } else {
            setStopBy('all');

            // 全ての立ち寄りの場合は到着地点に変える
            if (
              aggregationConfig?.dimension !== 'end' ||
              !includes(['count_per_area', 'count'], aggregationConfig.metric)
            ) {
              const metric = includes(
                ['count_per_area', 'count'],
                aggregationConfig?.metric
              )
                ? aggregationConfig.metric
                : 'count_per_area';
              const availableTypes = getAvailableAggregationTypes(
                'end',
                metric
              );
              startAggregation({
                type: includes(availableTypes, aggregationConfig?.type)
                  ? aggregationConfig.type
                  : availableTypes[0],
                dimension: 'end',
                metric: metric,
              });
            }
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ marginRight: '8px' }}>
            <FeatureIcon icon={faArrowRightArrowLeft} />
          </div>
          {t('stopByAll')}
        </div>
        {dataFilter.stopBy === 'all' && (
          <FeatureIcon icon={faCheck} size="14px" />
        )}
      </Content>
    </Collapse.Panel>
  );
};

const Content = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
  padding: 8px 6px;
  transition: 256ms;
  &:hover {
    background-color: rgba(0, 0, 0, 0.3);
  }
`;

export { StopByPanel };
